/**
 * One-time cross-subdomain auth handoff.
 *
 * Our own backend JWT is the real source of truth for authentication (see
 * useAuth.js) — Auth0 is only ever used ONCE, to establish identity. Moving
 * to a different {slug}.{rootDomain} workspace subdomain afterwards (first
 * landing after login, or an explicit workspace switch) must carry OUR OWN
 * JWT to the new origin directly, instead of re-running a second Auth0 SSO
 * round-trip per subdomain like the previous design did.
 *
 * The handoff travels as a URL *fragment* (`#auth=...`), never a query
 * param: fragments are never sent to the server (no log/Referer leakage)
 * and are stripped from the address bar immediately after being consumed
 * (see consumeHandoff), so it can't be replayed from browser history or a
 * shared link.
 *
 * No test runner is wired up for this package yet (see packages/auth's
 * package.json — no `test` script), so the pure encode/decode helpers are
 * kept separate from the localStorage/window side effects in consumeHandoff,
 * making them trivial to unit test once that's set up.
 */

import { syncIdentityKeysFromToken, tokenService } from './tokenService.js';
import { setCachedProfile } from './profileCache.js';

const HANDOFF_PREFIX = 'auth=';

/**
 * @param {{ token: string, profile?: { name?: string, email?: string, picture?: string } }} payload
 * @returns {string} fragment value, WITHOUT the leading '#'.
 */
export function encodeHandoff(payload) {
  return HANDOFF_PREFIX + encodeURIComponent(JSON.stringify(payload));
}

/**
 * @param {string} hash - window.location.hash, with or without leading '#'.
 * @returns {{ token: string, profile?: object }|null}
 */
export function decodeHandoff(hash) {
  const raw = (hash || '').replace(/^#/, '');
  if (!raw.startsWith(HANDOFF_PREFIX)) return null;
  try {
    const json = decodeURIComponent(raw.slice(HANDOFF_PREFIX.length));
    const payload = JSON.parse(json);
    if (!payload || typeof payload.token !== 'string' || !payload.token) return null;
    return payload;
  } catch {
    return null;
  }
}

/**
 * Attach a handoff fragment to a target URL built by
 * `resolveWorkspaceSubdomainUrl` (subdomain.js).
 *
 * @param {string|null} targetUrl
 * @param {{ token: string, profile?: object }} handoff
 * @returns {string|null}
 */
export function attachHandoff(targetUrl, handoff) {
  if (!targetUrl || !handoff?.token) return targetUrl;
  const base = targetUrl.split('#')[0];
  return `${base}#${encodeHandoff(handoff)}`;
}

/**
 * Consume a handoff fragment on THIS origin, if present: stores the JWT via
 * `tokenService.setToken`, mirrors the JWT's identity claims into the
 * `user_uuid` / `workspace_uuid` localStorage keys, caches the profile fields
 * (see profileCache.js) so the UI can display name/email/picture even though
 * Auth0 SDK has no session on this origin, and strips the fragment from the
 * address bar so it's never bookmark-able or re-playable.
 *
 * The identity keys are the part this used to skip. `localStorage` is
 * per-origin and the fragment only carries the token, so a subdomain reached
 * this way had a valid JWT and two empty keys — which is what the 11 readers
 * of those keys across 6 apps actually consult. See
 * `syncIdentityKeysFromToken` for the full account.
 *
 * Idempotent and safe to call more than once — after the first successful
 * consumption the hash no longer contains the marker, so later calls just
 * return `null` and do nothing.
 *
 * @returns {{ token: string, profile?: object }|null}
 */
export function consumeHandoff() {
  if (typeof window === 'undefined') return null;
  const payload = decodeHandoff(window.location.hash);
  if (!payload) return null;

  tokenService.setToken(payload.token);
  syncIdentityKeysFromToken(payload.token);
  if (payload.profile) setCachedProfile(payload.profile);

  // Strip the fragment immediately so reloading/sharing the URL can't replay it.
  const { pathname, search } = window.location;
  window.history.replaceState(null, '', `${pathname}${search}`);

  return payload;
}
